import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ExternalLink, ArrowLeft, ShieldAlert, Loader2, Coins } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AdSlot from "../components/AdSlot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Networks() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeOk, setIframeOk] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/networks`);
        if (!alive) return;
        setList(r.data.networks || []);
        if (id) {
          const d = await axios.get(`${API}/networks/${id}`);
          if (!alive) return;
          setDetail(d.data);
        } else {
          setDetail(null);
        }
      } catch (e) {
        console.error(e);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  // Reset iframe state when switching networks; assume blocked until proven loaded
  useEffect(() => {
    setIframeOk(false);
    // 3-second timeout: if no `onLoad` fires (X-Frame-Options blocked the load),
    // stay in fallback mode. Most major outlets will hit this path.
    const t = setTimeout(() => setIframeOk(false), 3000);
    return () => clearTimeout(t);
  }, [id]);

  if (!id) {
    return (
      <>
        <Header onOpenBrief={() => {}} />
        <main className="max-w-[1280px] mx-auto px-5 py-8">
          <div className="section-title">
            <span className="pip" />
            <h2 data-testid="networks-title">Networks · Compare the same story across outlets</h2>
          </div>
          <p className="text-[13.5px] text-[var(--muted)] mt-1 max-w-[820px]" data-testid="networks-tagline">
            One nav bar to bounce between the trusted outlets you already read. Newseze's trust score,
            bias score, and respect score layer on top — see how the same story is being framed,
            without leaving the network you came for.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-6">
            {list.map((n) => (
              <Link
                key={n.id}
                to={`/networks/${n.id}`}
                className="rounded-xl border border-[var(--line)] bg-white p-4 hover:border-[var(--blue)] hover:-translate-y-0.5 transition group"
                data-testid={`network-tile-${n.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block w-3 h-3 rounded-full" style={{ background: n.color }} />
                  <span className="font-black text-[14px] text-[var(--ink)] group-hover:text-[var(--blue)]">{n.name}</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)]">Verified newsroom · click to open</div>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-[12px] text-[var(--muted)] italic max-w-[820px]" data-testid="networks-disclaimer">
            Newseze does not host or alter content from these outlets. Each link respects the publisher's
            terms of service. When a publisher blocks embedded framing, we open the site in a new tab.
          </div>
          <div className="mt-6 rounded-2xl border border-[var(--gold)] bg-[#fff7d6] p-5 max-w-[820px]" data-testid="networks-bookmarklet-cta">
            <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[#7a5b00]">Newseze anywhere</div>
            <p className="text-[14px] text-[var(--ink)] mt-1.5">
              Want to earn credits on outlets we can't iframe (Fox, NYT, WSJ)?{" "}
              <Link to="/extension" className="font-black text-[var(--blue)] underline" data-testid="networks-bookmarklet-link">
                Install the Newseze bookmarklet →
              </Link>{" "}
              One drag and Newseze rides along on every news site you read.
            </p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const net = detail?.network;
  const scored = detail?.scored || [];

  return (
    <>
      <Header onOpenBrief={() => {}} />
      {/* Networks sub-nav — bounce between outlets */}
      <div className="bg-[var(--ink)] text-white border-b border-[var(--gold)]" data-testid="networks-subnav">
        <div className="max-w-[1380px] mx-auto px-3 overflow-x-auto flex items-center gap-1 py-1 whitespace-nowrap">
          <button onClick={() => navigate("/networks")} className="func-link !text-white !bg-transparent hover:!bg-white/10 !border-white/20" data-testid="networks-back">
            <ArrowLeft size={12} /> All networks
          </button>
          {list.map((n) => (
            <Link
              key={n.id}
              to={`/networks/${n.id}`}
              className={`func-link !text-white ${n.id === id ? "!bg-[var(--gold)] !text-[#1d1303] !border-[var(--gold)]" : "!bg-transparent hover:!bg-white/10 !border-white/20"}`}
              data-testid={`networks-tab-${n.id}`}
            >
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: n.color }} />
              {n.name}
            </Link>
          ))}
        </div>
      </div>

      {/* Top ad bar — unique Newseze get-paid-to-view advertising format */}
      <div className="bg-gradient-to-r from-[#fff7d6] to-white border-b border-[var(--gold)]" data-testid="networks-adbar">
        <div className="max-w-[1380px] mx-auto px-4 py-2 flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)] whitespace-nowrap">
            <Coins size={11} /> Earn +10 credits while reading {net?.name}
          </div>
          <div className="flex-1 min-h-[60px] flex items-center justify-center text-[11px] text-[var(--muted)] uppercase tracking-[0.14em] font-bold">
            Newseze Premium Ad Slot — $1 per 1000 views to Newseze readers
          </div>
        </div>
      </div>

      <main className="max-w-[1380px] mx-auto px-3 py-4">
        {loading || !net ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[var(--blue)]" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
            {/* iframe attempt with fallback */}
            <div className="rounded-xl border border-[var(--line)] bg-white overflow-hidden" style={{ minHeight: "70vh" }}>
              {iframeOk ? null : (
                <div className="p-6 flex flex-col items-center justify-center text-center min-h-[60vh]" data-testid="networks-fallback">
                  <ShieldAlert size={32} className="text-[var(--gold)] mb-3" />
                  <h3 className="text-[20px] font-black tracking-[-0.02em]">{net.name} blocks embedded framing.</h3>
                  <p className="text-[13.5px] text-[var(--muted)] mt-2 max-w-[560px]">
                    This publisher sets <code className="bg-[var(--bg)] px-1 rounded">X-Frame-Options: DENY</code> — a
                    security policy enforced by their servers. Open the site in a new tab below; Newseze's scored
                    summaries of their latest stories are shown to the right.
                  </p>
                  <a href={net.url} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
                     className="btn gold mt-5" data-testid="networks-open-external">
                    <ExternalLink size={14} /> Open {net.name} in new tab
                  </a>
                </div>
              )}
              <iframe
                title={net.name}
                src={net.url}
                onLoad={() => setIframeOk(true)}
                className={`w-full ${iframeOk ? "" : "hidden"}`}
                style={{ minHeight: "70vh", border: 0 }}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                data-testid="networks-iframe"
              />
            </div>

            {/* Newseze-scored sidebar */}
            <aside className="space-y-3">
              <div className="rounded-xl bg-gradient-to-br from-[#0a1f3d] to-[#1e40af] text-white p-4" data-testid="networks-sidebar-trust">
                <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)]">Newseze read of {net.name}</div>
                <p className="text-[13px] opacity-90 mt-1 leading-snug">
                  Stories below are pulled from {net.name}'s public RSS and scored by Newseze for trust,
                  balance, and respect.
                </p>
              </div>

              <div className="rounded-xl border border-[var(--line)] bg-white p-3" data-testid="networks-scored-list">
                <div className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--blue)] mb-2">Top scored stories</div>
                {scored.length === 0 ? (
                  <div className="text-[12.5px] text-[var(--muted)] italic py-3 text-center">No scored stories yet for this outlet — Newseze may not ingest its RSS.</div>
                ) : scored.map((s) => (
                  <Link to={`/article/${s.id}`} key={s.id} className="block py-2 border-b border-[var(--line)] last:border-0 hover:bg-[var(--bg)]" data-testid={`networks-scored-${s.id}`}>
                    <div className="text-[13px] font-bold text-[var(--ink)] leading-snug">{s.title}</div>
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--muted)] mt-1">
                      <span>{s.category}</span>
                      <span className="text-[var(--blue)]">trust {s.trust}</span>
                    </div>
                  </Link>
                ))}
              </div>

              <AdSlot size="h-rect" slot={`networks-${net.id}-sidebar`} />
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
